import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { FiInfo, FiMapPin } from "react-icons/fi";
import * as SmartySDK from "smartystreets-javascript-sdk";
import {
  fetchAddressAutocomplete,
  getAddressClientId,
  isAddressProxyEnabled,
} from "../../services/addressApi";
import {
  checkoutLabGroups,
  aoeAnswersToDict,
  programLabDetailKey,
  programLabDetail,
  programLabsRequiringAddress,
  requiresProgramLabCheckout,
  serviceableProgramLabCollectionOptions,
  selectedProgramLabAvailability,
  selectedProgramLabMethod,
  selectedCheckoutLabs,
  checkoutAvailabilityDecisionIds,
  getProgramLabAddressStateError,
  isProgramLabSelected,
} from "./programLabCheckout";
import {
  COLLECTION_METHOD_BANNERS,
  COLLECTION_METHOD_LABELS,
  CollectionMethodIcon,
  LabAoeQuestions,
  LabCollectionMethod,
  ProgramLabSubsetActions,
  PolicyModal,
} from "./ProgramLabCheckoutParts";
import ProgramLabCheckoutAddress from "./ProgramLabCheckoutAddress";
import DrawSiteSelector from "../labs/components/wizard/DrawSiteSelector";
import "./programLabCheckoutFields.css";
import "./ProgramLabCheckoutSubset.css";
const SMARTY_KEY = process.env.REACT_APP_SMARTY_EMBEDDED_KEY;

function useSmartyAutocompleteClient() {
  return useRef(() => {
    if (!SMARTY_KEY) return null;
    const credentials = new SmartySDK.core.SharedCredentials(SMARTY_KEY);
    return new SmartySDK.core.ClientBuilder(credentials)
      .withLicenses(["us-autocomplete-pro-cloud"])
      .buildUsAutocompleteProClient();
  }).current();
}
const labLineTotal = (lab, detail) => {
  const selected = selectedProgramLabMethod(lab, detail);
  const usesSelectedMethodPrice = lab?.is_combined === true
    && selected?.patient_price != null;
  const price = usesSelectedMethodPrice
    ? Number(selected.patient_price || 0)
    : Number(lab?.patient_price || 0);
  const shipping = usesSelectedMethodPrice
    ? Number(selected.shipping_fee || 0)
    : Number(lab?.shipping_fee || 0);
  return price + shipping;
};
const labAvailabilityResult = (lab, details) => {
  const members = lab?.members || [lab];
  const results = members
    .map((member) => programLabDetail(member, details).availability)
    .filter(Boolean);
  if (!results.length) return null;
  if (results.every((result) => result.is_available === true)) {
    return { status: "available", result: results[0] };
  }
  const unavailable = results.find((result) => result.is_available === false);
  return { status: "unavailable", result: unavailable || results[0] };
};
export default function ProgramLabCheckoutFields({
  checkout,
  value,
  onChange,
  availability,
  onCheckAvailability,
  onServiceAreaZipChange = () => {},
  mode = "collection",
  brandName = "Kin Meds",
  checkoutPolicies = null,
  checkoutPoliciesLoading = false,
  formError = "",
  runtimeAvailability = null,
  onContinueWithEligiblePrograms,
}) {
  const [openPolicy, setOpenPolicy] = useState(null);
  const [subsetBusy, setSubsetBusy] = useState(false);
  const [subsetError, setSubsetError] = useState("");
  const autocompleteClient = useSmartyAutocompleteClient();
  const autocompleteAbortRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const debounceRef = useRef(null);
  const [serviceAreaZip, setServiceAreaZip] = useState(
    () => value?.availability_zip || value?.address?.zip || "",
  );

  const fetchSuggestions = useCallback(async (street) => {
    if (street.length < 5) {
      setSuggestions([]);
      return;
    }
    const clientId = getAddressClientId();
    if (isAddressProxyEnabled() && clientId) {
      autocompleteAbortRef.current?.abort();
      const controller = new AbortController();
      autocompleteAbortRef.current = controller;
      try {
        const results = await fetchAddressAutocomplete(clientId, street, {
          maxResults: 6,
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setSuggestions(results);
          return;
        }
      } catch (error) {
        if (error?.name === "AbortError") {
          return;
        }
        console.warn("Address proxy autocomplete failed, trying embedded Smarty:", error);
      }
    }
    if (!autocompleteClient) {
      setSuggestions([]);
      return;
    }
    try {
      const lookup = new SmartySDK.usAutocompletePro.Lookup(street);
      lookup.maxResults = 6;
      const response = await autocompleteClient.send(lookup);
      setSuggestions(response.result || []);
    } catch {
      setSuggestions([]);
    }
  }, [autocompleteClient]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);
  useEffect(() => {
    if (value?.availability_zip) setServiceAreaZip(value.availability_zip);
  }, [value?.availability_zip]);

  const labs = checkoutLabGroups(checkout);
  if (!labs.length) return null;

  const isAoeStep = mode === "aoe";
  const isConsentStep = mode === "consents";
  const stageNumber = isConsentStep ? 3 : isAoeStep ? 2 : 1;
  const stageLabel = isConsentStep
    ? "Consents"
    : isAoeStep
      ? "Lab questions"
      : "Collection details";
  const address = value?.address || {};
  const addressStateError = getProgramLabAddressStateError(checkout, value);
  const consents = value?.consents || {};
  const selectedLabs = value?.labs || {};
  const labDetails = value?.lab_details || {};
  const hasCurrentAvailability = value?.availability_zip
    ? value.availability_zip === serviceAreaZip
      && (
        availability?.status === "available"
        || availability?.status === "unavailable"
        || selectedCheckoutLabs(checkout, value).some(
          (lab) => ["available", "unavailable"].includes(
            labAvailabilityResult(lab, labDetails)?.status,
          ),
        )
      )
    : ["available", "unavailable"].includes(availability?.status);

  const selectedLabKeys = new Set(
    selectedCheckoutLabs(checkout, value).map((lab) => programLabDetailKey(lab)),
  );
  const activeLabs = isConsentStep || isAoeStep
      ? labs.filter((lab) => (
        (lab.members || [lab]).some((member) => (
          selectedLabKeys.has(programLabDetailKey(member))
        ))
      ))
      : labs;

  const updateAddress = (key, next) => onChange({
    ...value,
    address: { ...address, [key]: next },
    address_verification: undefined,
    // Availability belongs to the checked service area, not the editable
    // patient address. Preserve it so a ZIP/state mismatch can be explained
    // inline just like the standalone checkout.
    lab_details: labDetails,
  });

  const updateConsent = (key, next) => onChange({
    ...value,
    consents: { ...consents, [key]: next },
  });

  const updateLab = (assignmentId, next) => onChange({
    ...value,
    labs: { ...selectedLabs, [assignmentId]: next },
  });

  const updateLabDetail = (assignmentId, key, next) => onChange({
    ...value,
    lab_details: {
      ...labDetails,
      [assignmentId]: {
        ...(labDetails[assignmentId] || {}),
        [key]: next,
        ...(key === "selectedMethod"
          ? { availability: undefined, availabilityDecisionId: undefined }
          : {}),
      },
    },
  });

  const updateLabGroupDetail = (lab, key, next) => {
    const nextDetails = { ...labDetails };
    (lab.members || [lab]).forEach((member) => {
      const memberKey = programLabDetailKey(member);
      nextDetails[memberKey] = {
        ...(nextDetails[memberKey] || {}),
        [key]: next,
      };
    });
    onChange({ ...value, lab_details: nextDetails });
  };

  const selectLabMethod = (lab, option) => {
    const members = lab.members || [lab];
    const nextDetails = { ...labDetails };
    members.forEach((member) => {
      const memberKey = programLabDetailKey(member);
      nextDetails[memberKey] = {
        ...(nextDetails[memberKey] || {}),
        selectedMethod: option?.method || "",
        selectedAssignmentId: option?.assignment_id || undefined,
        selectedSite: undefined,
      };
    });
    onChange({ ...value, lab_details: nextDetails });
  };

  const needsCollectionDetails = requiresProgramLabCheckout(checkout, value);
  const addressLabs = programLabsRequiringAddress(checkout, value);
  const addressLabNames = [...new Set(addressLabs.map((lab) => lab.name).filter(Boolean))];
  const addressMethods = new Set(addressLabs.map((lab) => (
    selectedProgramLabMethod(lab, programLabDetail(lab, labDetails))?.method
    || lab.collection_method
    || ""
  )));
  const addressPurpose = addressMethods.size === 1 && addressMethods.has("testkit")
    ? "We use this address to ship this lab kit and confirm that delivery is available in your area."
    : addressMethods.size === 1 && addressMethods.has("at_home_phlebotomy")
      ? "We use this address to schedule the at-home collection visit and confirm local availability."
      : addressMethods.size === 1 && ["walk_in_test", "draw_site", "on_site_collection"].some(
        (method) => addressMethods.has(method),
      )
        ? "Junction uses this address to create the lab order and requisition. You will still visit the selected draw site for collection."
        : "Junction uses this address to fulfill the selected lab orders and confirm local availability.";
  const formErrorIsShownOnLabCard = Boolean(formError) && labs.some((lab) => (
    (lab.members || [lab]).some((member) => (
      programLabDetail(member, labDetails).availability?.reason === formError
    ))
  ));

  const continueWithEligiblePrograms = async () => {
    const journey = runtimeAvailability?.journey;
    const eligibleProgramIds = (journey?.ready_program_ids || []).map(String);
    const acknowledgedExclusions = (journey?.blocked_program_ids || []).map(String);
    if (!eligibleProgramIds.length || !acknowledgedExclusions.length) return;
    setSubsetBusy(true);
    setSubsetError("");
    try {
      await onContinueWithEligiblePrograms({
        eligibleProgramIds,
        acknowledgedExclusions,
        availabilityDecisionIds: checkoutAvailabilityDecisionIds(
          checkout,
          value,
          eligibleProgramIds,
        ),
      });
    } catch (error) {
      setSubsetError(error?.message || "We could not update the selected Programs. Please retry.");
    } finally {
      setSubsetBusy(false);
    }
  };

  const handleStreetChange = (street) => {
    updateAddress("street", street);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(street), 350);
  };

  const applySuggestion = (suggestion) => {
    onChange({
      ...value,
      address: {
        ...address,
        street: suggestion.streetLine || "",
        apt: suggestion.secondary || address.apt || "",
        city: suggestion.city || "",
        state: suggestion.state || "",
        zip: suggestion.zipcode || address.zip || "",
      },
      address_verification: undefined,
      lab_details: labDetails,
    });
    setSuggestions([]);
  };

  const policyDocs = useMemo(() => ({
    hipaa: checkoutPolicies?.hipaa || {
      title: "HIPAA Authorization",
      version: "1.0",
      policy_type: "hipaa",
      content: "I authorize the use and disclosure of my health information for this lab order and necessary clinical treatment review.",
    },
    terms: checkoutPolicies?.terms || checkoutPolicies?.terms_of_service || {
      title: "Terms of Use",
      version: "1.0",
      policy_type: "terms_of_service",
      content: `I agree to the ${brandName} terms of use and privacy policy.`,
    },
    telehealth: checkoutPolicies?.telehealth || checkoutPolicies?.consent_telehealth || {
      title: "Telehealth Informed Consent",
      version: "1.0",
      policy_type: "consent_telehealth",
      content: "I consent to a telehealth evaluation by a licensed physician for this order.",
    },
  }), [checkoutPolicies, brandName]);

  const agreeAndClose = () => {
    if (openPolicy) {
      updateConsent(openPolicy, true);
      setOpenPolicy(null);
    }
  };

  return (
    <section className="program-lab-checkout" aria-label="Laboratory checkout">
      <div className="program-lab-checkout__stage" aria-label="Laboratory checkout step">
        <span>Step {stageNumber} of 3</span>
        <span aria-hidden="true">·</span>
        <strong>{stageLabel}</strong>
      </div>

      {/* Stage 1: Collection */}
      {!isAoeStep && !isConsentStep && (
        <div className="lcw-card">
          <div className="lcw-card-body">
            <h3 className="lcw-section-title lcw-section-title--icon">
              <FiMapPin aria-hidden="true" className="program-lab-checkout__section-icon" focusable="false" />
              <span>Where you'll get tested</span>
            </h3>
            <p className="lcw-section-sub">Enter your ZIP and we'll show the collection options available in your area.</p>
            <div className="lcw-zip-bar">
              <input
                className="lcw-zip-input"
                value={serviceAreaZip}
                onChange={(e) => {
                  const nextZip = e.target.value.replace(/\D/g, "").slice(0, 5);
                  setServiceAreaZip(nextZip);
                  onServiceAreaZipChange(nextZip);
                }}
                placeholder="Enter ZIP code"
                maxLength={5}
                onKeyDown={(e) => { if (e.key === "Enter") onCheckAvailability(serviceAreaZip); }}
              />
              <button
                type="button"
                className="lcw-zip-btn"
                aria-label="Check lab availability"
                onClick={() => onCheckAvailability(serviceAreaZip)}
                disabled={availability?.status === "loading" || serviceAreaZip.length < 5}
              >
                {availability?.status === "loading" ? "Checking…" : "Check"}
              </button>
            </div>
            {availability?.message && (
              <div className={`program-lab-checkout__avail-message program-lab-checkout__avail-message--${availability.status}`}>
                {availability.status === "available" && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                )}
                {availability.message}
              </div>
            )}
          </div>
        </div>
      )}

      {!isAoeStep && !isConsentStep && runtimeAvailability?.journey?.can_continue_with_subset && (
        <>
          <ProgramLabSubsetActions
            availability={runtimeAvailability}
            busy={subsetBusy}
            onContinue={continueWithEligiblePrograms}
          />
          {subsetError && <div className="lcw-error" role="alert">{subsetError}</div>}
        </>
      )}

      {/* List of labs for Collection and AOE */}
      {!isConsentStep && (
        <ul className="program-lab-checkout__labs">
          {labs.map((lab) => {
            const detailKey = programLabDetailKey(lab);
            const selectionKey = lab.patient_lab_group_is_shared
              && lab.patient_lab_group_id
              ? String(lab.patient_lab_group_id)
              : detailKey;
            const detail = programLabDetail(lab, labDetails);
            const lineTotal = lab.is_combined === true
              ? labLineTotal(lab, detail)
              : Number(
                lab.line_total
                ?? (
                  Number(lab.patient_price || 0)
                  + Number(lab.shipping_fee || 0)
                ),
            );
            const selectedMethod = selectedProgramLabMethod(lab, detail);
            const selectedMethodAvailability = selectedProgramLabAvailability(lab, detail);
            const availabilityResult = labAvailabilityResult(lab, labDetails);
            const isAvailable = availabilityResult?.status === "available";
            const isUnavailable = availabilityResult?.status === "unavailable";
            const isSelected = lab.is_required !== false
              || isProgramLabSelected(selectedLabs[selectionKey]);
            if (isAoeStep && !isSelected) return null;
            const methodKey = selectedMethod?.method || lab.collection_method || "draw_site";

            return (
              <li key={`${lab.program_id}:${lab.assignment_id}`} className="lcw-card lcw-card--lab">
                <div className="program-lab-checkout__lab-topline">
                  <span className="program-lab-checkout__requirement">
                    {lab.is_required === false ? "Optional" : "Required"}
                  </span>
                  <strong>${lineTotal.toFixed(2)}</strong>
                </div>
                <strong className="program-lab-checkout__lab-name">{lab.name}</strong>
                <div className="program-lab-checkout__lab-meta">
                  <span>
                    {lab.program_names?.length
                      ? lab.program_names.join(" and ")
                      : lab.program_name}
                  </span>
                  {selectedMethod?.method && (
                    <span className="program-lab-checkout__lab-method">
                      <CollectionMethodIcon method={selectedMethod.method} />
                      {COLLECTION_METHOD_LABELS[selectedMethod.method] || selectedMethod.method.replace(/_/g, " ")}
                    </span>
                  )}
                </div>
                {!isAoeStep && lab.is_required === false && (
                  <label className="program-lab-checkout__optional">
                    <input
                      type="checkbox"
                      checked={isProgramLabSelected(selectedLabs[selectionKey])}
                      disabled={isUnavailable && !isSelected}
                      onChange={(event) => updateLab(selectionKey, event.target.checked)}
                    />
                    <span>Include this lab in checkout</span>
                  </label>
                )}
                {!isAoeStep && lab.is_required === false && !isSelected && isUnavailable && (
                  <div className="lcw-unavail" role="status">
                    {availabilityResult.result?.reason
                      || "This optional Lab is not available at this location."}
                  </div>
                )}
                {isSelected && (
                  <>
                    {!isAoeStep && isUnavailable && (
                      <div className="lcw-unavail" role="alert">
                        {availabilityResult.result?.reason
                          || `This ${lab.is_required === false ? "optional" : "required"} Lab is not available at this location.`}
                      </div>
                    )}
                    {!isAoeStep && isAvailable && (
                      <>
                        <LabCollectionMethod
                          options={serviceableProgramLabCollectionOptions(lab, detail)}
                          selected={methodKey}
                          onSelect={(option) => selectLabMethod(lab, option)}
                        />
                        {["walk_in_test", "on_site_collection"].includes(methodKey) && (
                          <DrawSiteSelector
                            drawSites={selectedMethodAvailability?.drawSites || []}
                            pscCount={selectedMethodAvailability?.psc_count || 0}
                            zip={serviceAreaZip}
                            selectedSite={detail.selectedSite}
                            onSelect={(site) => updateLabDetail(detailKey, "selectedSite", site)}
                          />
                        )}
                        {COLLECTION_METHOD_BANNERS[methodKey] && (
                          <div className="lcw-method-banner">
                            <FiInfo size={15} />
                            <span>{COLLECTION_METHOD_BANNERS[methodKey]}</span>
                          </div>
                        )}
                      </>
                    )}
                    {isAoeStep && (
                      <LabAoeQuestions
                        lab={lab}
                        selectedMethod={selectedMethod}
                        answers={detail.aoeAnswers ?? detail.aoe_answers}
                        onAnswer={(key, val) => {
                          const next = {
                            ...aoeAnswersToDict(detail.aoeAnswers ?? detail.aoe_answers),
                            [key]: val,
                          };
                          updateLabGroupDetail(lab, "aoeAnswers", next);
                        }}
                      />
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Address Grid for Collection stage */}
      {!isAoeStep && !isConsentStep && needsCollectionDetails && addressLabs.length > 0 && hasCurrentAvailability && (
        <ProgramLabCheckoutAddress
          address={address}
          addressStateError={addressStateError}
          labNames={addressLabNames}
          purpose={addressPurpose}
          suggestions={suggestions}
          onStreetChange={handleStreetChange}
          onSuggestion={(suggestion) => {
            if (suggestion) applySuggestion(suggestion);
            else setSuggestions([]);
          }}
          onAddressChange={updateAddress}
        />
      )}

      {!isConsentStep
        && formError
        && (isAoeStep || !formErrorIsShownOnLabCard)
        && (
        <div className="lcw-error program-lab-checkout__form-error" role="alert">
          {formError}
        </div>
      )}

      {/* Stage 3: Consents / Review. Payment is collected once on the final
          treatment checkout page so treatment and lab charges use one card. */}
      {isConsentStep && (
        <div className="lcw-card">
          {/* Order Recap */}
          <div className="lcw-card-body">
            {activeLabs.map((lab) => {
              const detailKey = programLabDetailKey(lab);
              const detail = programLabDetail(lab, labDetails);
              const selectedMethod = selectedProgramLabMethod(lab, detail);
              const methodKey = selectedMethod?.method || lab.collection_method || "draw_site";
              const methodLabel = COLLECTION_METHOD_LABELS[methodKey]
                || selectedMethod?.label
                || "Walk-In lab draw";
              const lineTotal = lab.is_combined === true
                ? labLineTotal(lab, detail)
                : Number(
                  lab.line_total
                  ?? (
                    Number(lab.patient_price || 0)
                    + Number(lab.shipping_fee || 0)
                  ),
                );
              const programNames = lab.program_names?.length
                ? lab.program_names
                : [lab.program_name].filter(Boolean);
              return (
                <div key={`${lab.patient_lab_group_id || lab.program_id}:${lab.assignment_id || detailKey}`} className="lcw-review">
                  <div>
                    <div className="lcw-review-name">{lab.name}</div>
                    <div className="lcw-review-sub">{methodLabel}</div>
                    {programNames.length > 0 && (
                      <div className="lcw-review-sub">{programNames.join(" and ")}</div>
                    )}
                  </div>
                  <div className="lcw-review-price">${lineTotal.toFixed(2)}</div>
                </div>
              );
            })}
          </div>

          <div className="lcw-card-divider" />

          {/* Consents Section */}
          <div className="lcw-card-body">
            <div className="lcw-consents-title">Consents</div>
            <p className="lcw-consents-sub">Required before a physician can authorize your order.</p>

            {[
              {
                key: "hipaa",
                label: "HIPAA Authorization",
                desc: "I authorize the use and disclosure of my health information for this lab order.",
              },
              {
                key: "terms",
                label: "Terms of Use",
                desc: `I agree to the ${brandName} terms of use and privacy policy.`,
              },
              {
                key: "telehealth",
                label: "Telehealth Informed Consent",
                desc: "I consent to a telehealth evaluation by a licensed physician for this order.",
              },
            ].map(({ key, label, desc }) => {
              const isChecked = Boolean(consents[key]);
              return (
                <div key={key} className="lcw-consent-row">
                  <input
                    type="checkbox"
                    className="lcw-consent-check"
                    checked={isChecked}
                    onChange={(e) => updateConsent(key, e.target.checked)}
                    id={`consent-${key}`}
                    disabled={checkoutPoliciesLoading}
                  />
                  <label className="lcw-consent-label" htmlFor={`consent-${key}`}>
                    <span className="lcw-consent-title">{label}</span><br />
                    <span className="lcw-consent-desc">
                      {desc}{" "}
                      <button
                        type="button"
                        className="lcw-read-policy"
                        onClick={(e) => { e.preventDefault(); setOpenPolicy(key); }}
                      >
                        Read policy
                      </button>
                    </span>
                  </label>
                </div>
              );
            })}
          </div>

          {formError && <div className="lcw-error">{formError}</div>}
        </div>
      )}

      {openPolicy && (
        <PolicyModal
          policyDoc={policyDocs[openPolicy]}
          onClose={() => setOpenPolicy(null)}
          onAgree={agreeAndClose}
        />
      )}
    </section>
  );
}
